import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const TaskHierarchyTree = ({ 
  tasks, 
  selectedTask, 
  onTaskSelect, 
  onTaskExpand,
  expandedTasks 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'in-progress':
        return 'var(--color-primary)';
      case 'delayed':
        return 'var(--color-error)';
      case 'pending':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle2';
      case 'in-progress':
        return 'Clock';
      case 'delayed':
        return 'AlertCircle';
      case 'pending':
        return 'Circle';
      default:
        return 'Circle';
    }
  };

  const filteredTasks = tasks?.filter(task => {
    const matchesSearch = task?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || task?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const renderTaskNode = (task, level = 0) => {
    const isExpanded = expandedTasks?.includes(task?.id);
    const isSelected = selectedTask?.id === task?.id;
    const hasSubTasks = task?.subTasks && task?.subTasks?.length > 0;

    return (
      <div key={task?.id} className="w-full">
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all duration-250 ${
            isSelected ? 'bg-primary/10 border-l-2 border-primary' : 'hover:bg-muted'
          }`}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
          onClick={() => onTaskSelect(task)}
        >
          {hasSubTasks && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onTaskExpand(task?.id);
              }}
              className="flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-muted rounded transition-colors duration-250"
            >
              <Icon
                name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                size={16}
                color="var(--color-muted-foreground)"
              />
            </button>
          )}
          {!hasSubTasks && <div className="w-5" />}
          
          <Icon
            name={getStatusIcon(task?.status)}
            size={16}
            color={getStatusColor(task?.status)}
          />
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{task?.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="caption text-muted-foreground">{task?.duration}h</span>
              <span className="caption text-muted-foreground">•</span>
              <span className="caption text-muted-foreground">{task?.progress}%</span>
            </div>
          </div>

          {task?.dependencies && task?.dependencies?.length > 0 && (
            <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded bg-muted">
              <Icon name="GitBranch" size={12} color="var(--color-muted-foreground)" />
              <span className="caption text-muted-foreground">{task?.dependencies?.length}</span>
            </div>
          )}
        </div>
        {hasSubTasks && isExpanded && (
          <div className="mt-1">
            {task?.subTasks?.map(subTask => renderTaskNode(subTask, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-card border-r border-border">
      <div className="px-4 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4">Task Hierarchy</h2>
        
        <div className="relative mb-3">
          <Icon
            name="Search"
            size={16}
            color="var(--color-muted-foreground)"
            className="absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          {['all', 'pending', 'in-progress', 'completed', 'delayed']?.map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`flex-shrink-0 px-3 py-1 text-xs font-medium rounded-lg transition-colors duration-250 ${
                filterStatus === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {status?.charAt(0)?.toUpperCase() + status?.slice(1)?.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {filteredTasks?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Icon name="Search" size={48} color="var(--color-muted-foreground)" />
            <p className="mt-4 text-sm text-muted-foreground text-center">
              No tasks found matching your criteria
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredTasks?.map(task => renderTaskNode(task))}
          </div>
        )}
      </div>
      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total Tasks</span>
          <span className="font-semibold text-foreground">{tasks?.length}</span>
        </div>
      </div>
    </div>
  );
};

export default TaskHierarchyTree;